import { MediaDisplay } from './MediaDisplay';

/**
 * MetaStoryCollectionMockup - Instagram/Facebook Story 9:16 Collection Ad Mockup
 *
 * Dimensions: 420px × 747px (9:16 aspect ratio)
 *
 * Structure (matching Instagram Story collection ad format):
 * - Top Header: Profile picture, username, "Sponsored" label, close button
 * - Full-screen Background: Hero image/video
 * - Right Side Actions: Heart, message, share icons
 * - Bottom: Product cards grid overlay + CTA button
 *
 * Note: imageUrl props can contain either image or video URLs
 *
 * Usage:
 *   <MetaStoryCollectionMockup
 *     username="Ford"
 *     heroImageUrl="/path/to/hero.jpg"
 *     products={[
 *       { imageUrl: '/product1.jpg', title: 'Mustang' },
 *       { imageUrl: '/product2.jpg', title: 'F-150' },
 *       { imageUrl: '/product3.jpg', title: 'Explorer' },
 *     ]}
 *     ctaText="Shop Now"
 *   />
 */

interface ProductItem {
  imageUrl?: string;
  title?: string;
  price?: string;
}

interface MetaStoryCollectionMockupProps {
  username?: string;
  profilePicUrl?: string;
  heroImageUrl?: string;
  products?: ProductItem[];
  ctaText?: string;
  websiteUrl?: string;
}

export function MetaStoryCollectionMockup({
  username = 'Ford',
  profilePicUrl,
  heroImageUrl,
  products = [],
  ctaText = 'Shop Now',
  websiteUrl = 'ford.com',
}: MetaStoryCollectionMockupProps) {
  // Ensure we have at least 3 product slots
  const displayProducts = [
    products[0] || {},
    products[1] || {},
    products[2] || {},
  ];

  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      {/* Full-screen Background Image/Video */}
      <div className="absolute inset-0">
        {heroImageUrl ? (
          <MediaDisplay
            url={heroImageUrl}
            alt="Collection hero"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <div className="text-center text-white/50">
              <div className="text-4xl font-bold mb-2">9:16</div>
              <div className="text-sm">Hero Image</div>
            </div>
          </div>
        )}
      </div>

      {/* Gradient overlays for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

      {/* Story progress bars at very top */}
      <div className="absolute top-2 left-3 right-3 z-30 flex gap-1">
        <div className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
          <div className="w-full h-full bg-white rounded-full" />
        </div>
        <div className="flex-1 h-0.5 bg-white/30 rounded-full" />
        <div className="flex-1 h-0.5 bg-white/30 rounded-full" />
      </div>

      {/* Top Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between"
        style={{ padding: '16px 12px 0' }}
      >
        {/* Left: Profile & Username */}
        <div className="flex items-center" style={{ gap: '10px' }}>
          {/* Profile Picture - Circular with gradient border */}
          <div
            className="rounded-full p-[2px]"
            style={{
              background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
            }}
          >
            <div
              className="bg-black rounded-full overflow-hidden flex items-center justify-center"
              style={{ width: '32px', height: '32px' }}
            >
              {profilePicUrl ? (
                <img
                  src={profilePicUrl}
                  alt={username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-sm">
                  {username.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Username & Sponsored */}
          <div className="flex items-center gap-2">
            <span
              className="text-white font-semibold"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              {username}
            </span>
            <span className="text-white/80" style={{ fontSize: '13px' }}>
              •
            </span>
            <span
              className="text-white/80"
              style={{ fontSize: '13px', lineHeight: '18px' }}
            >
              Sponsored
            </span>
          </div>
        </div>

        {/* Right: Close button */}
        <button className="text-white/90 hover:text-white">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Right Side Actions */}
      <div
        className="absolute z-20 flex flex-col items-center"
        style={{ right: '12px', bottom: '220px', gap: '20px' }}
      >
        {/* Heart */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Message */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Share/Send */}
        <button className="flex flex-col items-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Bottom Section - Product Grid + CTA */}
      <div className="absolute bottom-0 left-0 right-0 z-20" style={{ padding: '0 12px 16px' }}>
        {/* Product Cards Grid */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {displayProducts.map((product, idx) => (
            <div
              key={idx}
              className="bg-white/95 rounded-lg overflow-hidden"
              style={{ backdropFilter: 'blur(8px)' }}
            >
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {product.imageUrl ? (
                  <MediaDisplay
                    url={product.imageUrl}
                    alt={product.title || `Product ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <div className="text-lg font-bold">{idx + 1}</div>
                  </div>
                )}
              </div>
              <div className="p-1.5">
                <p className="text-[10px] font-medium text-gray-900 line-clamp-1">
                  {product.title || `Product ${idx + 1}`}
                </p>
                {product.price && (
                  <p className="text-[9px] text-gray-600">{product.price}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <button
          className="w-full flex items-center justify-center text-white font-semibold"
          style={{
            height: '44px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            borderRadius: '22px',
            fontSize: '15px',
            letterSpacing: '0.3px',
          }}
        >
          {ctaText}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="ml-2"
          >
            <path
              d="M5 12H19M12 5L19 12L12 19"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Website URL below button */}
        <span
          className="block text-center text-white/70 mt-2"
          style={{ fontSize: '12px' }}
        >
          {websiteUrl}
        </span>
      </div>
    </div>
  );
}
